import { DELETION_FLAG, FacilityKey, OrganizationKey, OrganizationKeySchema } from "@packages/core";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuClipboardCopy, LuPlus } from "react-icons/lu";
import { Merge } from "type-fest";
import * as v from "valibot";

import {
  ButtonStyle2,
  LinkGroup,
  OrganizationSelector,
  PageName,
  PageNumberSelector,
  PageSizeSelector,
  SkeletonScreen,
  TABLE_STYLE,
  Tooltip,
  copyToClipboard,
} from "#components";
import {
  useApplicationConfigurationQuery,
  useFacilityInfiniteQuery,
  useInstallationInfiniteQuery,
  useListedOrganizations,
  useOrganizationQuery,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/facilities/")({
  component: Component,
  validateSearch: v.object({
    organizationKey: v.fallback(v.optional(OrganizationKeySchema()), undefined),
    pageSize: v.fallback(
      v.optional(
        v.pipe(v.union([v.number(), v.pipe(v.string(), v.toNumber())]), v.integer(), v.minValue(1)),
      ),
      undefined,
    ),
  }),
});

function Component() {
  const { organizationKey, pageSize = 100 } = Route.useSearch();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pageNumber, setPageNumber] = useState(0);

  const facilityInfiniteQuery = useFacilityInfiniteQuery(
    organizationKey
      ? {
          index: "organizationKey-deleted-gsi",
          condition: [["organizationKey", "=", organizationKey]],
          limit: pageSize,
        }
      : { limit: pageSize },
  );

  const { facilities, numberOfFacilities } = useMemo(
    function () {
      return {
        facilities: facilityInfiniteQuery.data?.pages.at(pageNumber)?.facilities ?? [],
        numberOfFacilities:
          facilityInfiniteQuery.data?.pages.reduce(function (numberOfFacilities, page) {
            return numberOfFacilities + page.facilities.length;
          }, 0) ?? 0,
      };
    },
    [facilityInfiniteQuery.data, pageNumber],
  );

  const organizationPaginator = useListedOrganizations({ limit: 25 });
  const organizationQuery = useOrganizationQuery(organizationKey);

  return (
    <main className={css({ display: "flex", flexDirection: "column" })}>
      <PageName>{t("%FacilityManagement.0%")}</PageName>

      <p className={css({ fontSize: "24px", fontWeight: 700 })}>{t("%CustomerManagement.0%")}</p>

      <LinkGroup
        className={css({ marginTop: "16px" })}
        links={[
          {
            available: true,
            label: t("%OrganizationInformation.0%"),
            path: "/management/organizations",
          },
          {
            available: true,
            label: t("%FacilityInformation.0%"),
            path: "/management/facilities",
          },
        ]}
      />

      <div
        className={css({
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
        })}
      >
        <div>
          <OrganizationSelector
            className={css({ maxWidth: "480px", width: "100%" })}
            load={async function () {
              await organizationPaginator.fetchNextPage();
            }}
            loaded={organizationPaginator.loaded}
            loading={organizationPaginator.loading}
            onChange={function (organization) {
              setPageNumber(0);
              navigate({
                replace: true,
                search(parameters) {
                  return { ...parameters, organizationKey: organization?.organizationKey };
                },
                to: ".",
              });
            }}
            options={organizationPaginator.organizations}
            title={t("%Organization.0%")}
            value={organizationQuery.data ?? null}
          />
        </div>
        <div>
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px" })}
            to="/management/facilities/registration"
          >
            <LuPlus />
            <p>{t("%AddFacility.0%")}</p>
          </Link>
        </div>
      </div>

      <div
        className={css(TABLE_STYLE, {
          flexGrow: 1,
          gridTemplateColumns: "128px 140px minmax(148px, 1fr) 160px 136px",
          marginTop: "6px",
          marginBottom: "auto",
          maxHeight: "max-content",
        })}
      >
        <div data-part="head" data-scope="table">
          <div data-part="head-row" data-scope="table">
            <div data-part="head-cell" data-scope="table">
              {t("%OrganizationName.0%")}
            </div>
            <div data-part="head-cell" data-scope="table">
              {t("%FacilityKey.0%")}
            </div>
            <div data-part="head-cell" data-scope="table">
              {t("%FacilityName.0%")}
            </div>
            <div data-part="head-cell" data-scope="table">
              {t("%UpdateStatus.0%")}
            </div>
            <div data-part="head-cell" data-scope="table" />
          </div>
        </div>

        <div data-part="body" data-scope="table">
          {facilities.map(function (facility) {
            return (
              <div data-part="body-row" data-scope="table" key={facility.facilityKey}>
                <OrganizationNameCell
                  data-part="body-cell"
                  data-scope="table"
                  organizationKey={facility.organizationKey}
                />
                <div data-part="body-cell" data-scope="table">
                  <Tooltip
                    content={
                      <div className={css({ display: "flex", alignItems: "center", gap: "8px" })}>
                        <LuClipboardCopy className={css({ height: "14px", width: "auto" })} />
                        <span className={css({ backgroundColor: "muted-background" })}>
                          {facility.facilityKey}
                        </span>
                      </div>
                    }
                    trigger={
                      <span
                        className={css({
                          cursor: "pointer",
                          _hover: { fontWeight: 500, textDecoration: "underline" },
                          _active: { fontWeight: 700 },
                        })}
                        onClick={function () {
                          copyToClipboard(facility.facilityKey);
                        }}
                      >
                        {facility.facilityKey}
                      </span>
                    }
                  />
                </div>
                <div data-part="body-cell" data-scope="table">
                  {facility.name}
                </div>
                <UpdateStatusCell
                  data-part="body-cell"
                  data-scope="table"
                  facilityKey={facility.facilityKey}
                />
                <div data-part="body-cell" data-scope="table">
                  <Link
                    className={ButtonStyle2({ color: "black" })}
                    params={facility}
                    to="/facilities/$facilityKey/residents"
                  >
                    {t("%FacilityPage.0%")}
                  </Link>
                </div>
              </div>
            );
          })}

          {facilityInfiniteQuery.isFetching &&
            Array.from({ length: Math.max(pageSize - facilities.length, 0) }, function (_, index) {
              return (
                <div data-part="body-row" data-scope="table" key={index}>
                  <div
                    className={css({ gridColumn: "1 / -1" })}
                    data-part="body-cell"
                    data-scope="table"
                  >
                    <SkeletonScreen
                      className={css({ borderRadius: "6px", width: "max(10%, 128px)" })}
                    >
                      &nbsp;
                    </SkeletonScreen>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className={css({ display: "flex", justifyContent: "space-between", marginTop: "8px" })}>
        <PageNumberSelector
          hasNextPage={facilityInfiniteQuery.hasNextPage}
          isFetching={facilityInfiniteQuery.isFetching}
          numberOfItems={numberOfFacilities}
          onNextPage={function () {
            if (facilityInfiniteQuery.hasNextPage && !facilityInfiniteQuery.isFetching) {
              setPageNumber(function (pageNumber) {
                return pageNumber + 1;
              });
              facilityInfiniteQuery.fetchNextPage().then(function (facilityInfiniteQuery) {
                setPageNumber(function (pageNumber) {
                  const numberOfFacilities =
                    facilityInfiniteQuery.data?.pages.reduce(function (numberOfFacilities, page) {
                      return numberOfFacilities + page.facilities.length;
                    }, 0) ?? 0;
                  return Math.max(
                    Math.min(pageNumber + 1, Math.ceil(numberOfFacilities / pageSize) - 1),
                    0,
                  );
                });
              });
            }
          }}
          pageNumber={pageNumber}
          pageSize={pageSize}
          setPageNumber={setPageNumber}
        />
        <PageSizeSelector
          onChange={function (pageSize) {
            setPageNumber(0);
            navigate({
              replace: true,
              search(parameters) {
                return { ...parameters, pageSize };
              },
              to: ".",
            });
          }}
          options={
            new Set(
              [pageSize, 50, 100, 150].sort(function (a, b) {
                return a - b;
              }),
            )
          }
          value={pageSize}
        />
      </div>
    </main>
  );
}

function OrganizationNameCell({
  organizationKey,
  ...props
}: Merge<React.ComponentProps<"div">, { organizationKey: OrganizationKey }>) {
  const organizationQuery = useOrganizationQuery(organizationKey);

  return (
    <div {...props}>
      <SkeletonScreen
        className={css({ borderRadius: "6px" })}
        disabled={organizationQuery.isFetched}
      >
        {organizationQuery.data?.name ?? "　　　"}
      </SkeletonScreen>
    </div>
  );
}

function UpdateStatusCell({
  facilityKey,
  ...props
}: Merge<React.ComponentProps<"div">, { facilityKey: FacilityKey }>) {
  const { t } = useTranslation();

  const applicationConfigurationQuery = useApplicationConfigurationQuery();
  const installationInfiniteQuery = useInstallationInfiniteQuery({
    index: "facilityKey-deleted",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  const { numberOfInstallations, numberOfOutdatedInstallations, numberOfUpdatedInstallations } =
    useMemo(
      function () {
        let numberOfInstallations = 0;
        let numberOfUpdatedInstallations = 0;
        let numberOfOutdatedInstallations = 0;

        if (
          !applicationConfigurationQuery.data ||
          !applicationConfigurationQuery.data.android ||
          !applicationConfigurationQuery.data.ios ||
          !installationInfiniteQuery.data
        ) {
          return {
            numberOfInstallations,
            numberOfOutdatedInstallations,
            numberOfUpdatedInstallations,
          };
        }

        for (const page of installationInfiniteQuery.data.pages) {
          for (const installation of page.installations) {
            numberOfInstallations++;
            switch (installation.edition) {
              case "Android":
                if (
                  installation.revision <
                  applicationConfigurationQuery.data.android.requiredRevision
                ) {
                  numberOfOutdatedInstallations++;
                } else {
                  numberOfUpdatedInstallations++;
                }
                break;
              case "Ios":
                if (
                  installation.revision < applicationConfigurationQuery.data.ios.requiredRevision
                ) {
                  numberOfOutdatedInstallations++;
                } else {
                  numberOfUpdatedInstallations++;
                }
                break;
              default:
                break;
            }
          }
        }

        return {
          numberOfInstallations,
          numberOfOutdatedInstallations,
          numberOfUpdatedInstallations,
        };
      },
      [applicationConfigurationQuery.data, installationInfiniteQuery.data],
    );

  return (
    <div {...props}>
      <SkeletonScreen
        className={css({ borderRadius: "6px" })}
        disabled={applicationConfigurationQuery.isFetched && installationInfiniteQuery.isFetched}
      >
        {numberOfInstallations > 0
          ? numberOfOutdatedInstallations > 0
            ? numberOfUpdatedInstallations +
              " / " +
              (numberOfUpdatedInstallations + numberOfOutdatedInstallations) +
              " (" +
              (
                (numberOfUpdatedInstallations /
                  (numberOfUpdatedInstallations + numberOfOutdatedInstallations)) *
                100
              ).toFixed(2) +
              "%)"
            : numberOfUpdatedInstallations + " (100%)"
          : t("%NoInstallations.0%")}
      </SkeletonScreen>
    </div>
  );
}
